// src/segments.ts
var MINUTE_MS = 6e4;
var BERLIN_TZ = "Europe/Berlin";
function minutesBetween(a, b) {
  return Math.round((b.getTime() - a.getTime()) / MINUTE_MS);
}
function berlinEndOfDay(at) {
  const day = berlinDay(at);
  const offsetMs = berlinOffsetMs(/* @__PURE__ */ new Date(`${day}T12:00:00Z`));
  return new Date(Date.parse(`${day}T23:59:59.999Z`) - offsetMs);
}
function berlinOffsetMs(at) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: BERLIN_TZ,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  const map = dtf.formatToParts(at).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return asUtc - at.getTime();
}
function berlinDay(at) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BERLIN_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(at);
}
function berlinToday(at = /* @__PURE__ */ new Date()) {
  return berlinDay(at);
}
function berlinMinuteOfDay(at) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BERLIN_TZ,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit"
  }).formatToParts(at);
  const map = parts.reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  return Number(map.hour) % 24 * 60 + Number(map.minute);
}
var TIMING_TYPES = /* @__PURE__ */ new Set([
  "TRAVEL_START",
  "WORK_START",
  "PAUSE_START",
  "PAUSE_END",
  "STOP",
  "TECH_DONE",
  // R7 (Entscheidung Faruq/Tobias 2026-08-26): die Problemmeldung sperrt den
  // Auftrag fuer den Monteur — sie STOPPT deshalb auch die Uhr. Vorher lief
  // das offene Segment serverseitig bis zur R4-Tagesend-Schliessung weiter
  // (und wurde so abgerechnet), waehrend die App laengst Stopp anzeigte.
  // PROBLEM_RESOLVED bleibt bewusst wirkungslos: Weiterarbeit beginnt mit
  // einem neuen, expliziten WORK_/TRAVEL_START.
  "PROBLEM_REPORTED"
]);
function computeSegments(events, opts) {
  const sorted = [...events].sort((a, b) => {
    const t = a.occurredAt.getTime() - b.occurredAt.getTime();
    return t !== 0 ? t : a.id < b.id ? -1 : 1;
  });
  const segments = [];
  const effects = {};
  let open = null;
  let paused = false;
  const now = opts?.now;
  const suspect = (e) => now !== void 0 && e.occurredAt.getTime() > now.getTime() + 15 * MINUTE_MS;
  const close = (openSeg, endEvent, extra) => {
    const flags = [...extra];
    if (suspect(openSeg.startEvent) || suspect(endEvent)) flags.push("SUSPECT_CLOCK");
    segments.push({
      category: openSeg.category,
      startAt: openSeg.startEvent.occurredAt,
      endAt: endEvent.occurredAt,
      minutes: minutesBetween(openSeg.startEvent.occurredAt, endEvent.occurredAt),
      flags,
      eventIds: [openSeg.startEvent.id, endEvent.id]
    });
    open = null;
  };
  for (const e of sorted) {
    if (!TIMING_TYPES.has(e.type)) {
      effects[e.id] = "effective";
      continue;
    }
    switch (e.type) {
      case "TRAVEL_START":
      case "WORK_START": {
        if (open) close(open, e, ["IMPLICIT_CLOSE"]);
        open = { category: e.type === "TRAVEL_START" ? "TRAVEL" : "WORK", startEvent: e };
        paused = false;
        effects[e.id] = "effective";
        break;
      }
      case "PAUSE_START": {
        if (open && open.category === "WORK") {
          close(open, e, []);
          paused = true;
          effects[e.id] = "effective";
        } else {
          effects[e.id] = "noop";
        }
        break;
      }
      case "PAUSE_END": {
        if (paused) {
          open = { category: "WORK", startEvent: e };
          paused = false;
          effects[e.id] = "effective";
        } else {
          effects[e.id] = "noop";
        }
        break;
      }
      case "STOP":
      case "TECH_DONE":
      case "PROBLEM_REPORTED":
      // R7: Problemmeldung stoppt die Uhr (s. o.)
      default: {
        if (open) {
          close(open, e, []);
          effects[e.id] = "effective";
        } else {
          effects[e.id] = "noop";
        }
        paused = false;
        break;
      }
    }
  }
  if (open && now !== void 0) {
    const eod = berlinEndOfDay(open.startEvent.occurredAt);
    if (eod.getTime() <= now.getTime()) {
      segments.push({
        category: open.category,
        startAt: open.startEvent.occurredAt,
        endAt: eod,
        minutes: minutesBetween(open.startEvent.occurredAt, eod),
        flags: ["AUTO_CLOSED_EOD"],
        eventIds: [open.startEvent.id]
      });
    }
  }
  return { segments, effects };
}

// src/attendance.ts
function isValidClockTransition(state, kind) {
  return kind === "IN" && state === "OUT" || kind === "OUT" && state !== "OUT" || kind === "BREAK_START" && state === "PRESENT" || kind === "BREAK_END" && state === "BREAK";
}
var BREAK_VIOLATION_FLAGS = [
  "BREAK_MISSING",
  "BREAK_TOO_SHORT",
  "BREAK_SEGMENT_TOO_SHORT",
  "SIX_HOUR_RULE"
];
var FLAG_ORDER = [
  "MISSING_OUT",
  "DOUBLE_IN",
  "ORPHAN_OUT",
  "BREAK_OPEN_AT_END",
  "BREAK_MISSING",
  "BREAK_TOO_SHORT",
  "BREAK_SEGMENT_TOO_SHORT",
  "SIX_HOUR_RULE",
  "REST_LT_11H"
];
var DEFAULT_DAY_CONFIG = {
  break1OverMin: 360,
  break1Min: 30,
  break2OverMin: 540,
  break2Min: 45,
  minSegmentMin: 15,
  sixHourLimitMin: 360,
  dayEndMinute: 1439
};
function mandatoryBreak(netMinutes, config) {
  if (netMinutes > config.break2OverMin) return config.break2Min;
  if (netMinutes > config.break1OverMin) return config.break1Min;
  return 0;
}
function pflichtpauseLevel(netMinutes, creditableBreakMin, config = DEFAULT_DAY_CONFIG) {
  const mandatory = mandatoryBreak(netMinutes, config);
  if (mandatory === 0 || creditableBreakMin >= mandatory) return "none";
  return netMinutes > config.break2OverMin ? "red" : "amber";
}
function workIntervals(pairs, breaks) {
  const result = [];
  for (const [inM, outM] of pairs) {
    const within = breaks.filter(([bs, be]) => bs >= inM && be <= outM).sort((a, b) => a[0] - b[0]);
    let cursor = inM;
    for (const [bs, be] of within) {
      result.push([cursor, bs]);
      cursor = be;
    }
    result.push([cursor, outM]);
  }
  return result;
}
function computeAttendanceDay(events, config = DEFAULT_DAY_CONFIG) {
  const sorted = [...events].sort((a, b) => a.minute - b.minute);
  const flags = /* @__PURE__ */ new Set();
  const pairs = [];
  const breakSegments = [];
  let sessionOpen = null;
  let breakOpen = null;
  for (const e of sorted) {
    if (e.kind === "IN") {
      if (breakOpen !== null) {
        breakSegments.push([breakOpen, e.minute]);
        breakOpen = null;
      }
      if (sessionOpen !== null) {
        flags.add("DOUBLE_IN");
        pairs.push([sessionOpen, e.minute]);
      }
      sessionOpen = e.minute;
    } else if (e.kind === "OUT") {
      if (sessionOpen === null) {
        flags.add("ORPHAN_OUT");
        continue;
      }
      if (breakOpen !== null) {
        breakSegments.push([breakOpen, e.minute]);
        breakOpen = null;
        flags.add("BREAK_OPEN_AT_END");
      }
      pairs.push([sessionOpen, e.minute]);
      sessionOpen = null;
    } else if (e.kind === "BREAK_START") {
      if (sessionOpen === null) continue;
      if (breakOpen !== null) breakSegments.push([breakOpen, e.minute]);
      breakOpen = e.minute;
    } else {
      if (breakOpen === null) continue;
      breakSegments.push([breakOpen, e.minute]);
      breakOpen = null;
    }
  }
  if (sessionOpen !== null) {
    flags.add("MISSING_OUT");
    if (breakOpen !== null) {
      breakSegments.push([breakOpen, config.dayEndMinute]);
      breakOpen = null;
      flags.add("BREAK_OPEN_AT_END");
    }
    pairs.push([sessionOpen, config.dayEndMinute]);
  }
  if (pairs.length === 0) {
    return {
      grossMinutes: 0,
      breakMinutes: 0,
      creditableBreakMin: 0,
      mandatoryBreakMin: 0,
      netMinutes: 0,
      breakSegments: [],
      flags: FLAG_ORDER.filter((f) => flags.has(f)),
      pairs
    };
  }
  const grossMinutes = pairs.reduce((s, [a, b]) => s + (b - a), 0);
  const breakMinutes = breakSegments.reduce((s, [a, b]) => s + (b - a), 0);
  const creditableBreakMin = breakSegments.reduce(
    (s, [a, b]) => s + (b - a >= config.minSegmentMin ? b - a : 0),
    0
  );
  const missingOut = flags.has("MISSING_OUT");
  const netMinutes = missingOut ? 0 : grossMinutes - breakMinutes;
  const mandatoryBreakMin = missingOut ? 0 : mandatoryBreak(netMinutes, config);
  if (!missingOut && mandatoryBreakMin > 0) {
    if (breakSegments.length === 0) flags.add("BREAK_MISSING");
    else if (creditableBreakMin < mandatoryBreakMin) flags.add("BREAK_TOO_SHORT");
    if (breakSegments.some(([a, b]) => b - a < config.minSegmentMin)) {
      flags.add("BREAK_SEGMENT_TOO_SHORT");
    }
    for (const [a, b] of workIntervals(pairs, breakSegments)) {
      if (b - a > config.sixHourLimitMin) {
        flags.add("SIX_HOUR_RULE");
        break;
      }
    }
  }
  return {
    grossMinutes,
    breakMinutes,
    creditableBreakMin,
    mandatoryBreakMin,
    netMinutes,
    breakSegments,
    flags: FLAG_ORDER.filter((f) => flags.has(f)),
    pairs
  };
}
var ABSENCE_MATRIX = {
  VACATION: {
    fillsTarget: true,
    creditsIst: true,
    consumes: "VACATION",
    requiresNote: false,
    selfService: true
  },
  BETRIEBSURLAUB: {
    fillsTarget: true,
    creditsIst: true,
    consumes: "VACATION",
    requiresNote: false,
    selfService: false
  },
  SICK: {
    fillsTarget: true,
    creditsIst: true,
    consumes: "NONE",
    requiresNote: false,
    selfService: true
  },
  KIND_KRANK: {
    fillsTarget: true,
    creditsIst: false,
    consumes: "NONE",
    requiresNote: false,
    selfService: false
  },
  BESCHAEFTIGUNGSVERBOT: {
    fillsTarget: true,
    creditsIst: true,
    consumes: "NONE",
    requiresNote: false,
    selfService: false
  },
  SONDERURLAUB: {
    fillsTarget: true,
    creditsIst: true,
    consumes: "SONDERURLAUB",
    requiresNote: false,
    selfService: false
  },
  UNBEZAHLT: {
    fillsTarget: true,
    creditsIst: false,
    consumes: "NONE",
    requiresNote: false,
    selfService: false
  },
  UEBERSTUNDEN_AUSGLEICH: {
    fillsTarget: false,
    creditsIst: false,
    consumes: "NONE",
    requiresNote: false,
    selfService: true
  },
  SONSTIGES: {
    fillsTarget: true,
    creditsIst: false,
    consumes: "NONE",
    requiresNote: true,
    selfService: false
  }
};
function absenceEffect(kind) {
  return ABSENCE_MATRIX[kind] ?? ABSENCE_MATRIX.SONSTIGES;
}
var ABSENCE_KINDS = Object.keys(ABSENCE_MATRIX);
function scheduledDayMinutes(schedule, isoWeekday) {
  const week = [
    schedule.minMon,
    schedule.minTue,
    schedule.minWed,
    schedule.minThu,
    schedule.minFri,
    schedule.minSat,
    schedule.minSun
  ];
  return week[isoWeekday - 1] ?? 0;
}
function uniformSchedule(weeklyHours) {
  const day = Math.round(weeklyHours * 60 / 5);
  return { minMon: day, minTue: day, minWed: day, minThu: day, minFri: day, minSat: 0, minSun: 0 };
}
function dayTargetMinutes(baseMinutes, ctx) {
  if (baseMinutes <= 0) return 0;
  if (ctx.isHoliday) return 0;
  if (ctx.absence) {
    const eff = absenceEffect(ctx.absence.kind);
    if (!eff.fillsTarget) return baseMinutes;
    if (eff.creditsIst) return baseMinutes;
    return ctx.absence.halfDay ? Math.round(baseMinutes / 2) : 0;
  }
  return baseMinutes;
}
function dayAbsenceCreditMinutes(baseMinutes, ctx) {
  if (baseMinutes <= 0 || ctx.isHoliday || !ctx.absence) return 0;
  const eff = absenceEffect(ctx.absence.kind);
  if (!eff.fillsTarget || !eff.creditsIst) return 0;
  return ctx.absence.halfDay ? Math.round(baseMinutes / 2) : baseMinutes;
}
function dailyTargetMinutes(weeklyHours, ctx) {
  const base = scheduledDayMinutes(uniformSchedule(weeklyHours), ctx.isoWeekday);
  return dayTargetMinutes(base, ctx);
}
function minutesToHM(minutes) {
  const sign = minutes < 0 ? "-" : "";
  const abs = Math.abs(minutes);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}

// src/status.ts
function deriveOrderStatus(input) {
  const { current, assignments, anyWorkStarted } = input;
  if (current === "DRAFT" || current === "BILLED" || current === "CLOSED" || current === "CANCELLED") {
    return current;
  }
  if (input.anyCompletionDone) return "DONE";
  const active = assignments.filter((a) => a.revokedAt === null);
  if (active.length > 0 && active.every((a) => a.techDoneAt !== null)) {
    return "DONE";
  }
  if (anyWorkStarted && current === "ACTIVE") {
    return "IN_PROGRESS";
  }
  return current;
}

// src/planning.ts
function hhmmToMinutes(value) {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
function overlaps(a, b) {
  if (a.start === null || a.end === null || b.start === null || b.end === null) return false;
  return a.start < b.end && b.start < a.end;
}
export {
  ABSENCE_KINDS,
  BREAK_VIOLATION_FLAGS,
  DEFAULT_DAY_CONFIG,
  absenceEffect,
  berlinDay,
  berlinEndOfDay,
  berlinMinuteOfDay,
  berlinToday,
  computeAttendanceDay,
  computeSegments,
  dailyTargetMinutes,
  dayAbsenceCreditMinutes,
  dayTargetMinutes,
  deriveOrderStatus,
  hhmmToMinutes,
  isValidClockTransition,
  mandatoryBreak,
  minutesToHM,
  overlaps,
  pflichtpauseLevel,
  scheduledDayMinutes,
  uniformSchedule
};
//# sourceMappingURL=index.js.map